import React from "react";
import { CategoryWithThemes } from "./types";
import { ApiResponseMention } from "../../api/types/api-response";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Minus } from "lucide-react";

// Predefined color palette for categories - works in both light and dark mode
const CATEGORY_COLORS = [
  { bg: "bg-blue-100 dark:bg-blue-950/50", text: "text-blue-900 dark:text-blue-100", border: "border-blue-200 dark:border-blue-800/50" },
  { bg: "bg-purple-100 dark:bg-purple-950/50", text: "text-purple-900 dark:text-purple-100", border: "border-purple-200 dark:border-purple-800/50" },
  { bg: "bg-amber-100 dark:bg-amber-950/50", text: "text-amber-900 dark:text-amber-100", border: "border-amber-200 dark:border-amber-800/50" },
  { bg: "bg-pink-100 dark:bg-pink-950/50", text: "text-pink-900 dark:text-pink-100", border: "border-pink-200 dark:border-pink-800/50" },
  { bg: "bg-cyan-100 dark:bg-cyan-950/50", text: "text-cyan-900 dark:text-cyan-100", border: "border-cyan-200 dark:border-cyan-800/50" },
  { bg: "bg-emerald-100 dark:bg-emerald-950/50", text: "text-emerald-900 dark:text-emerald-100", border: "border-emerald-200 dark:border-emerald-800/50" },
  { bg: "bg-orange-100 dark:bg-orange-950/50", text: "text-orange-900 dark:text-orange-100", border: "border-orange-200 dark:border-orange-800/50" },
  { bg: "bg-indigo-100 dark:bg-indigo-950/50", text: "text-indigo-900 dark:text-indigo-100", border: "border-indigo-200 dark:border-indigo-800/50" },
  { bg: "bg-teal-100 dark:bg-teal-950/50", text: "text-teal-900 dark:text-teal-100", border: "border-teal-200 dark:border-teal-800/50" },
  { bg: "bg-rose-100 dark:bg-rose-950/50", text: "text-rose-900 dark:text-rose-100", border: "border-rose-200 dark:border-rose-800/50" },
];

const getCategoryColor = (categoryId: string, allCategories: CategoryWithThemes[]) => {
  const index = allCategories.findIndex(cat => cat.id === categoryId);
  return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
};

export const CategoriesWithThemesListModern: React.FC<{
  categoriesWithThemes: CategoryWithThemes[];
  onThemeClick: (mention: ApiResponseMention) => void;
}> = (props) => {
  const [hoveredTheme, setHoveredTheme] = React.useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = React.useState<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (themeId: string) => {
    const timeout = setTimeout(() => {
      setHoveredTheme(themeId);
    }, 1000); // 1000ms hover delay
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setHoveredTheme(null);
  };

  return (
    <div className="grid grid-cols-4 gap-2">
      {props.categoriesWithThemes.map((categoryWithThemes) => {
        const categoryColor = getCategoryColor(categoryWithThemes.id, props.categoriesWithThemes);

        return (
          <div key={categoryWithThemes.id} className={`flex flex-col gap-1 p-2 rounded-md ${categoryColor.bg}`}>
            {/* Category Header */}
            <h3 className={`text-center font-bold text-sm py-1 ${categoryColor.text}`}>
              {categoryWithThemes.attributes.name}
            </h3>

            {/* Themes */}
            {categoryWithThemes.themes.map((theme) => {
              const isHovered = hoveredTheme === theme.id;

              return (
                <div
                  key={`theme-${theme.id}`}
                  className="inline-flex rounded-md shadow-sm"
                  role="group"
                  onMouseEnter={() => handleMouseEnter(theme.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Button
                    size="sm"
                    className="bg-green-100 text-green-800 hover:bg-green-200 dark:bg-[rgb(34,74,54)] dark:text-green-100 dark:hover:bg-[rgb(44,84,64)] w-12 rounded-r-none"
                    onClick={() =>
                      props.onThemeClick({
                        sentiment: 1,
                        theme_id: Number(theme.id),
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mx-auto" />
                  </Button>

                  <Popover open={isHovered} onOpenChange={() => {}}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 justify-center text-center overflow-hidden text-ellipsis whitespace-nowrap min-w-0 rounded-none border-l-0 border-r-0 bg-card hover:bg-accent"
                        onClick={() =>
                          props.onThemeClick({
                            sentiment: 0,
                            theme_id: Number(theme.id),
                          })
                        }
                      >
                        {theme.attributes.name}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-3">
                        <h4 className="font-semibold text-sm">
                          {theme.attributes.name}
                        </h4>
                        {theme.attributes.description && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                            <p className="text-sm text-foreground">
                              {theme.attributes.description}
                            </p>
                          </div>
                        )}
                        {theme.attributes.examples && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Examples</p>
                            <p className="text-sm text-foreground">
                              {theme.attributes.examples}
                            </p>
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Button
                    size="sm"
                    className="bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-100 dark:hover:bg-red-800 w-12 rounded-l-none"
                    onClick={() =>
                      props.onThemeClick({
                        sentiment: -1,
                        theme_id: Number(theme.id),
                      })
                    }
                  >
                    <Minus className="h-4 w-4 mx-auto" />
                  </Button>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
